<template>
  <div class="flex relative px-6 py-5 gap-4">
    <div class="flex-1">
      <template v-if="review.includedReviewItems.value.length">
        <div
          class="inline-grid w-full border-t border-r border-neutral-border-default rounded-lg"
          style="grid-template-columns: 420px 1fr"
        >
          <div class="bg-neutral-background-default h-12.5 rounded-tl-lg sticky top-13 left-0 z-51">
            <div
              class="h-full text-sm px-5 py-3.5 justify-between border-neutral-border-default border-b border-l flex items-center font-medium rounded-tl-lg"
            >
              <span class="text-neutral-text-title">Extracted Records</span>
              <FloatingMenu placement="right-start" :arrow="false">
                <template #activator="{ show, isShown }">
                  <div
                    class="p-1 rounded-xl bg-neutral-background-secondary border border-neutral-border-default"
                  >
                    <button
                      class="relative flex items-center justify-center w-8 h-8 rounded-lg bg-neutral-background-default shadow-sm border border-neutral-border-default"
                      :class="{ 'bg-neutral-background-secondary': isShown }"
                      @click="show"
                    >
                      <i-material-symbols-search class="size-5 text-neutral-text-default" />
                      <span v-if="searchQuery" class="absolute -top-1 -right-1 flex h-2.5 w-2.5">
                        <span
                          class="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"
                        ></span>
                        <span
                          class="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500 border-2 border-white"
                        ></span>
                      </span>
                    </button>
                  </div>
                </template>

                <template #content>
                  <TextInput
                    v-model="searchQuery"
                    class="w-full"
                    multiline
                    input-classes="border-none"
                    placeholder="Search extracted text..."
                  />

                  <button
                    v-if="searchQuery"
                    class="absolute right-2 top-2"
                    @click="searchQuery = ''"
                  >
                    <i-material-symbols-close class="size-4 text-neutral-text-subtitle" />
                  </button>
                </template>
              </FloatingMenu>
            </div>
          </div>

          <div class="bg-neutral-background-default h-12.5 sticky top-13 left-0 z-40 rounded-r-lg">
            <div
              :title="sortedAttributes[selectedAttributeIndex]?.question"
              class="h-full text-sm border-l border-b border-neutral-border-default text-neutral-text-title flex p-3.5 items-center font-medium justify-between"
            >
              <div class="truncate">
                {{
                  sortedAttributes[selectedAttributeIndex]?.label !== ""
                    ? sortedAttributes[selectedAttributeIndex]?.label
                    : "Question unspecified"
                }}
                – Synthesis
              </div>
            </div>
          </div>

          <div
            class="border-l border-b bg-white border-neutral-border-default overflow-y-auto h-[calc(100vh-140px)] rounded-bl-lg"
          >
            <template v-if="reviewItemsWithExtraction.length">
              <div
                v-for="reviewItem in reviewItemsWithExtraction"
                :key="reviewItem.id"
                class="h-fit px-5 py-2"
              >
                <ReviewItemDataExtractionCard
                  :review-item="reviewItem"
                  :item-url="reviewItem.metadata.url"
                  :extracted-text="reviewItem.extractedText"
                />
              </div>
            </template>

            <div v-else class="p-4 text-center text-neutral-text-subtitle">
              No articles match your filters
            </div>
          </div>

          <div class="h-[calc(100vh-140px)]">
            <AttributeSynthesis
              :key="sortedAttributes[selectedAttributeIndex]?.id"
              :attribute-structure="sortedAttributes[selectedAttributeIndex]"
            />
          </div>
        </div>
      </template>

      <div v-else-if="review.entity.value?.studies?.length" class="h-full">
        No articles have been included
      </div>
      <div v-else>This review is empty</div>
    </div>

    <div
      v-if="review.includedReviewItems.value.length"
      class="w-[260px] sticky top-13 right-0 h-[calc(100vh-80px)] overflow-y-auto flex flex-col items-start gap-3 z-1"
    >
      <button
        v-for="(attribute, index) in sortedAttributes"
        :key="attribute.id"
        class="px-3.5 py-2 w-full text-left rounded-lg transition-colors flex justify-between hover:bg-white hover:shadow hover:shadow-black/10"
        :class="{
          'bg-white shadow shadow-black/10 text-neutral-text-title':
            index === selectedAttributeIndex,
          'text-gray-400 hover:bg-white': index !== selectedAttributeIndex,
        }"
        @click="selectedAttributeIndex = index"
      >
        <span
          v-if="attribute.label !== ''"
          :title="attribute.label"
          class="text-base font-medium truncate"
        >
          {{ attribute.label }}
        </span>
        <router-link
          v-else
          v-tooltip.top="'Click to add the missing label in the plan'"
          :to="`/reviews/${review.entity.value.id}/plan/objective`"
          class="text-base font-medium truncate"
          @click.stop
        >
          Question unspecified
        </router-link>
        <CheckCircleIcon
          v-if="attributeSynthesisFinalizationState[index]"
          class="size-6 text-green-500 shrink-0"
        />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { injectStrict } from "@app/utils/injectStrict";
import { CheckCircleIcon } from "@heroicons/vue/16/solid";
import { ReviewKey } from "../../use-review";
import AttributeSynthesis from "./AttributeSynthesis.vue";
import ReviewItemDataExtractionCard from "./ReviewItemDataExtractionCard.vue";
import FloatingMenu from "@app/components/Global/FloatingMenu.vue";
import TextInput from "@app/components/Global/Inputs/TextInput.vue";

const review = injectStrict(ReviewKey);

const searchQuery = ref("");

const sortedAttributes = computed(() =>
  review.entity.value.plan!.synthesisPlan!.attributesStructure.toSorted((a, b) => {
    if (a.order === null && b.order === null) return 0;
    if (a.order === null) return -1;
    if (b.order === null) return 1;
    return a.order > b.order ? 1 : -1;
  }),
);

const attributeSynthesisFinalizationState = computed(() =>
  sortedAttributes.value.map(
    (attr) =>
      review.entity.value.plan?.synthesisPlan.attributesStructure.find((p) => p.id === attr.id)
        ?.isSynthesisFinalized === true,
  ),
);

const selectedAttributeIndex = ref(0);

const selectedAttributeId = computed(
  () => sortedAttributes.value[selectedAttributeIndex.value]?.id,
);

const reviewItemsWithExtraction = computed(() => {
  const attributeId = selectedAttributeId.value;
  if (!attributeId) return [];

  return review.filteredIncludedReviewItems.value
    .filter((item) =>
      item.synthesis?.attributes?.some(
        (attr) => attr.attributeStructureId === attributeId && attr.isFinalized === true,
      ),
    )
    .map((item) => ({
      ...item,
      extractedText: getHocuspocusText(item.id, attributeId),
    }))
    .filter((item) => {
      const text = item.extractedText.toLowerCase().trim();
      return text && text !== "not reported";
    })
    .filter((item) => {
      if (!searchQuery.value.trim()) return true;
      return item.extractedText.toLowerCase().includes(searchQuery.value.toLowerCase().trim());
    });
});

function getHocuspocusText(studyId: number, attributeId: string): string {
  if (!attributeId) return "";

  const field = `study::${studyId}|attribute::${attributeId}`;
  const fragment = review.dataExtractionProvider.document.getXmlFragment(field);

  let text = "";

  fragment.toArray().forEach((node: any) => {
    if (node.text) {
      text += node.text;
    } else if (node.toString) {
      text += node.toString().replaceAll(/<\/?[^>]+>/g, "");
    }
  });

  return text.trim();
}
</script>
